/**
 * HomeContent - Home 画面のボディ部分。
 *
 * ウィジェット・フォルダ・アイコンを 1 つの HomeGrid に流し込むことで、
 * 並べ替え・サイズ変更・非表示を全部同じ仕組みで扱えるようにしている。
 */

import { History, Library, Loader2, Sparkles, Upload } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

import { HomeGrid, type HomeGridItem } from '@/components/home/home-grid'
import { HOME_WIDGETS, useAvailableWidgetKeys } from '@/components/home/home-widget-registry'
import type { HomeWidgetsData } from '@/components/home/use-home-widgets'
import { DomainSelector } from '@/components/shared/domain-selector'
import { IconFolder } from '@/components/shared/icon-folder'
import { Button } from '@/components/ui/button'
import { ENGLISH_PATHS, FOLDER_PATHS, NAV_ICON_MAP } from '@/lib/nav-icons'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

type HomeContentProps = {
  /** ナビゲーション時のコールバック */
  onNavigate: (path: string) => void
  /** ウィジェットに渡す集計データ */
  data: HomeWidgetsData
  /** パスごとに出すバッジの件数。0 以下は表示しない */
  badges?: Map<string, number>
}

/** 常用しないので既定では並べず、編集モードの「追加」から出させるページ */
const DEFAULT_HIDDEN_PATHS: string[] = [PATHS.CHECK]

const IconBadge = ({ count }: { count: number }) => (
  <span className="absolute -top-1 -right-1 flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-[10px] leading-none font-semibold text-white tabular-nums">
    {count > 99 ? '99+' : count}
  </span>
)

/** グリッド 1 セル（正方形）に収まるアイコン。フォルダやアクションも見た目を揃える */
const IconTile = ({
  label,
  badge = 0,
  onClick,
  children,
}: {
  label: string
  badge?: number
  onClick: () => void
  children: ReactNode
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-1">
    <div
      onClick={onClick}
      className="relative flex size-14 cursor-pointer items-center justify-center shadow-md"
      aria-label={badge > 0 ? `${label} (${badge})` : label}
    >
      {children}
      {badge > 0 && <IconBadge count={badge} />}
    </div>
    <span className="w-full truncate text-center text-[10px] leading-none">{label}</span>
  </div>
)

/** アイコンが 1 つも無いときの表示。/users/me の応答前や失敗時に「権限なし」と出さない */
const EmptyHomeState = () => {
  const authSyncStatus = useAppStore((state) => state.authSyncStatus)
  const refreshUserData = useAppStore((state) => state.refreshUserData)

  if (authSyncStatus === 'idle' || authSyncStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <div className="text-muted-foreground">読み込み中...</div>
      </div>
    )
  }

  if (authSyncStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <div className="text-muted-foreground">ユーザー情報を取得できませんでした</div>
        <Button variant="outline" onClick={() => void refreshUserData()}>
          再試行
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="text-muted-foreground mb-2">アクセス可能なコンテンツがありません</div>
      <div className="text-sm text-muted-foreground">管理者に連絡して、アクセス権を追加してもらってください</div>
    </div>
  )
}

export const HomeContent = ({ onNavigate, data, badges }: HomeContentProps) => {
  const accessiblePages = useAppStore((state) => state.accessiblePages)
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const availableWidgetKeys = useAvailableWidgetKeys(data)

  const { navItems: allNavItems, folderItems } = useMemo(() => {
    if (!accessiblePages || accessiblePages.length === 0) {
      return { navItems: [], folderItems: [] }
    }
    const offlineProductsItem = {
      path: PATHS.OFFLINE_PRODUCTS,
      label: 'ダウンロード済み',
      Icon: NAV_ICON_MAP[PATHS.OFFLINE_PRODUCTS],
    }
    const historyItem = {
      path: '/p?order_by=last_read',
      label: '履歴',
      Icon: () => <History size={50} color="white" />,
    }
    const newArrivalItem = {
      path: '/p?order_by=access_granted_at',
      label: '新着',
      Icon: () => <Sparkles size={50} color="white" />,
    }

    if (hasFullAccess) {
      const domainItems = accessiblePages
        .filter((page) => FOLDER_PATHS.includes(page.path) && NAV_ICON_MAP[page.path])
        .map((page) => ({
          path: page.path,
          label: page.label,
          Icon: NAV_ICON_MAP[page.path],
        }))

      const featureItems = accessiblePages
        .filter((page) => !FOLDER_PATHS.includes(page.path) && NAV_ICON_MAP[page.path])
        .map((page) => ({
          path: page.path,
          label: page.label,
          Icon: NAV_ICON_MAP[page.path],
        }))

      domainItems.push(historyItem)
      domainItems.push(offlineProductsItem)
      return { navItems: featureItems, folderItems: domainItems }
    }

    const domainPages = accessiblePages.filter((page) => FOLDER_PATHS.includes(page.path))
    const nonDomainPages = accessiblePages.filter(
      (page) => !FOLDER_PATHS.includes(page.path) && NAV_ICON_MAP[page.path],
    )

    const items = nonDomainPages.map((page) => ({
      path: page.path,
      label: page.label,
      Icon: NAV_ICON_MAP[page.path],
    }))

    if (domainPages.length > 0) {
      items.unshift({
        path: '/p?domain=all',
        label: 'ライブラリ',
        Icon: () => <Library size={50} color="white" />,
      })
    }

    items.push(newArrivalItem)
    items.push(historyItem)
    items.push(offlineProductsItem)

    return { navItems: items, folderItems: [] }
  }, [accessiblePages, hasFullAccess])

  // 英語系は単独で並べず、2つ以上あるときだけフォルダにまとめる
  const { navItems, englishItems } = useMemo(() => {
    const english = allNavItems.filter((item) => ENGLISH_PATHS.includes(item.path))
    if (english.length < 2) return { navItems: allNavItems, englishItems: [] }
    return { navItems: allNavItems.filter((item) => !ENGLISH_PATHS.includes(item.path)), englishItems: english }
  }, [allNavItems])

  const gridItems = useMemo<HomeGridItem[]>(() => {
    const items: HomeGridItem[] = []

    for (const widget of HOME_WIDGETS) {
      if (!availableWidgetKeys.has(widget.key)) continue
      items.push({
        key: widget.key,
        label: widget.label,
        defaultSize: widget.defaultSize,
        fixedSize: widget.fixedSize,
        render: (size) => widget.render(data, size, onNavigate),
      })
    }

    if (folderItems.length > 0) {
      items.push({
        key: 'folder:contents',
        label: 'コンテンツ',
        render: () => <IconFolder label="コンテンツ" items={folderItems} onNavigate={onNavigate} />,
      })
    }
    if (englishItems.length > 0) {
      items.push({
        key: 'folder:english',
        label: '英語',
        render: () => <IconFolder label="英語" items={englishItems} onNavigate={onNavigate} />,
      })
    }

    for (const { path, label, Icon } of navItems) {
      items.push({
        key: `page:${path}`,
        label,
        defaultHidden: DEFAULT_HIDDEN_PATHS.includes(path),
        render: () => (
          <IconTile label={label} badge={badges?.get(path) ?? 0} onClick={() => onNavigate(path)}>
            <Icon />
          </IconTile>
        ),
      })
    }

    items.push({
      key: 'action:domain-selector',
      label: 'コンテンツ選択',
      render: () => (
        <DomainSelector iconMap={NAV_ICON_MAP}>
          {(openSheet) => (
            <IconTile label="コンテンツ選択" onClick={openSheet}>
              <Library size={50} color="white" />
            </IconTile>
          )}
        </DomainSelector>
      ),
    })

    if (hasFullAccess) {
      items.push({
        key: 'action:import',
        label: 'インポート',
        render: () => (
          <IconTile label="インポート" onClick={() => onNavigate(PATHS.IMPORT)}>
            <Upload size={50} color="white" />
          </IconTile>
        ),
      })
    }

    return items
  }, [availableWidgetKeys, data, onNavigate, folderItems, englishItems, navItems, badges, hasFullAccess])

  return (
    <>
      {navItems.length === 0 && folderItems.length === 0 && englishItems.length === 0 ? (
        <EmptyHomeState />
      ) : (
        <HomeGrid items={gridItems} />
      )}
    </>
  )
}
